<template>
	<div class="flex gap-3">
		<a class="no-underline" :href="itemLink" aria-label="Open Product Link">
			<figure role="figure" class="w-[72px]" :aria-label="item.title">
				<img :src="item.image" rel="preload" :alt="item.title" class="w-full object-contain" />
			</figure>
		</a>
		<div class="flex flex-col items-start">
			<h4 class="text-xs/[18px] font-medium">{{ itemCategory }}</h4>
			<p v-if="item.variantLabel" class="text-xs/[18px] font-normal !m-0 !p-0">
				{{ item.variantLabel }}: {{ item.shortTitle }}
			</p>
			<p class="text-xs/[18px] font-medium !m-0 !p-0 !mt-1.5">
				{{ item.price }}
			</p>
			<div class="mt-auto flex gap-3">
				<button
					v-if="item.outOfStock"
					type="button"
					disabled
					class="disabled:opacity-25 disabled:pointer-events-none disabled:cursor-not-allowed text-xs/[18px] font-normal"
					aria-label="Item Out of Stock"
				>
					Out of Stock
				</button>
				<button
					v-else-if="!isReleased"
					type="button"
					disabled
					class="disabled:opacity-25 disabled:pointer-events-none disabled:no-underline disabled:cursor-not-allowed text-xs/[18px] font-normal flex items-end gap-2"
					aria-label="Item Pending Release"
				>
					<span v-html="formattedReleaseDate" />
				</button>
				<button
					v-else
					type="button"
					class="disabled:opacity-25 disabled:no-underline disabled:pointer-events-none disabled:cursor-not-allowed text-xs/[18px] font-normal underline underline-offset-2"
					:disabled="loading"
					@click="handleAddToCart"
					aria-label="Add to Bag"
				>
					{{ addToBagTxt }}
				</button>
				<button
					v-if="isLoggedIn"
					type="button"
					@click="handleAddToList"
					aria-label="Add to List"
					class="text-xs/[18px] font-normal underline underline-offset-2"
				>
					{{ addToListTxt }}
				</button>
			</div>
		</div>
	</div>
</template>
<script setup>
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

import { useSessionStore, useCartStore } from '@stores'

const props = defineProps({
	item: {
		type: Object,
		required: true,
	},
})
const emit = defineEmits(['list-add'])

const loading = ref(false)
const addToBagTxt = ref('Add to bag')
const addToListTxt = ref('Add to list')

const sessionStore = useSessionStore()
const { shoppingContext, isLoggedIn } = storeToRefs(sessionStore)
const cartStore = useCartStore()
const { addToCart } = cartStore

const itemLink = computed(() =>
	route(`shop.${shoppingContext.value}.item.index`, {
		...props.item.linkParams,
	})
)
const itemCategory = computed(() => props.item.title.split('-')[0].trim())
const isReleased = computed(() => {
	if (!props.item.releaseDateForCustomer) return true
	const today = dayjs()
	return today.isAfter(props.item.releaseDateForCustomer)
})
const formattedReleaseDate = computed(() => {
	const d = dayjs(props.item.releaseDateForCustomer).tz(props.item.timezone)
	const [month, dayParts] = d.format('MMMM Do').split(' ')
	const day = dayParts.slice(0, -2)
	const ordinal = dayParts.slice(-2)
	const time = d.format('hha')
	return `Available ${month} ${day}<sup>${ordinal}</sup> at ${time}`
})

const handleAddToList = () => {
	emit('list-add', props.item)
}
const handleAddToCart = async () => {
	try {
		loading.value = true
		const { itemId } = props.item
		const items = [
			{
				id: itemId,
				quantity: 1,
			},
		]
		addToBagTxt.value = 'Adding'
		await addToCart({ items })
		addToBagTxt.value = 'Added!'
		loading.value = false
		setTimeout(() => {
			addToBagTxt.value = 'Add to bag'
		}, 1200)
	} catch (_) {
		addToBagTxt.value = 'Failed'
		setTimeout(() => {
			addToBagTxt.value = 'Add to bag'
		}, 1200)
	} finally {
		loading.value = false
	}
}
</script>
